"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { lowercaseWords } from "@/lib/tool-kit/text-analysis/tokens";

export interface DiversityStats {
  totalWords: number;
  uniqueWords: number;
  ttr: number;
  rootTtr: number;
  topWords: { word: string; count: number }[];
}

/** Letters in any script, digits and internal apostrophes (straight or curly); a hyphen splits. */
function tokenize(text: string): string[] {
  return lowercaseWords(text, { hyphens: "split" });
}

export function computeStats(text: string): DiversityStats | null {
  const tokens = tokenize(text);
  if (tokens.length === 0) {
    return null;
  }
  const counts = new Map<string, number>();
  for (const token of tokens) {
    counts.set(token, (counts.get(token) ?? 0) + 1);
  }
  const topWords = [...counts.entries()]
    .filter(([, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 10)
    .map(([word, count]) => ({ word, count }));
  return {
    totalWords: tokens.length,
    uniqueWords: counts.size,
    ttr: counts.size / tokens.length,
    rootTtr: counts.size / Math.sqrt(tokens.length),
    topWords,
  };
}

type Strings = {
  [K in keyof typeof LEXICAL_DIVERSITY_CALCULATOR_STRINGS]: string;
};

/**
 * Rough interpretation band for a TTR value. Bands are heuristics for
 * passages of a few hundred words — TTR is not comparable across lengths.
 */
function interpretTtr(
  ttr: number,
  strings: Strings
): { label: string; detail: string } {
  if (ttr < 0.4) {
    return {
      label: strings.lowVarietyLabel,
      detail: strings.lowVarietyDetail,
    };
  }
  if (ttr < 0.6) {
    return {
      label: strings.typicalVarietyLabel,
      detail: strings.typicalVarietyDetail,
    };
  }
  return {
    label: strings.highVarietyLabel,
    detail: strings.highVarietyDetail,
  };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const LEXICAL_DIVERSITY_CALCULATOR_STRINGS = {
  yourText: "Your text",
  pasteAParagraphAnEssay:
    "Paste a paragraph, an essay section, or a full draft here...",
  calculate: "Calculate",
  clear: "Clear",
  totalWords: "Total words",
  uniqueWords: "Unique words",
  ttr: "TTR",
  rootTtr: "Root TTR",
  importantCaveat: "Important caveat:",
  ttrFallsAsTextsGet:
    'TTR falls as texts get longer — every text repeats function words like "the" and "of" more as it grows. Only compare TTR between texts of similar length; for moderately different lengths, root TTR is the fairer comparison.',
  mostRepeatedWords: "Most repeated words",
  word: "Word",
  count: "Count",
  lowVarietyLabel: "Low variety",
  lowVarietyDetail:
    "A large share of words repeat. For a short-to-medium passage this often reads as repetitive — look at the most-repeated words below for candidates to vary. (For long texts, a low TTR is expected and not a problem by itself.)",
  typicalVarietyLabel: "Typical variety",
  typicalVarietyDetail:
    "This range is common for clear expository prose of a few hundred words — repetition of key terms is normal and often aids readability.",
  highVarietyLabel: "High variety",
  highVarietyDetail:
    "Most words appear only once, indicating a varied vocabulary. Very short texts score high almost automatically, so weigh this against the word count.",
} as const;

export default function LexicalDiversityCalculatorWidget() {
  const [input, setInput] = useState("");
  const [stats, setStats] = useState<DiversityStats | null>(null);

  function handleCalculate() {
    const computed = computeStats(input);
    if (computed) {
      setStats(computed);
    }
  }

  function handleClear() {
    setInput("");
    setStats(null);
  }

  const strings = useToolUi(LEXICAL_DIVERSITY_CALCULATOR_STRINGS);
  const interpretation = stats ? interpretTtr(stats.ttr, strings) : null;

  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="diversity-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[240px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="diversity-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.pasteAParagraphAnEssay}
          value={input}
        />
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleCalculate}
          type="button"
        >
          {strings.calculate}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && stats === null}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label={strings.totalWords} value={fmt(stats.totalWords)} />
          <StatCard
            label={strings.uniqueWords}
            value={fmt(stats.uniqueWords)}
          />
          <StatCard
            label={strings.ttr}
            value={`${fmt(stats.ttr * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
          />
          <StatCard
            label={strings.rootTtr}
            value={fmt(stats.rootTtr, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          />
        </div>
      )}
      {/* Interpretation */}
      {stats && interpretation && (
        <section className="space-y-3 rounded-lg border border-border bg-card p-6">
          <h2 className="font-semibold text-foreground text-lg">
            {interpretation.label}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {interpretation.detail}
          </p>
          <p className="rounded-lg bg-muted/50 p-4 text-muted-foreground text-sm leading-relaxed">
            <span className="font-medium text-foreground">
              {strings.importantCaveat}
            </span>{" "}
            {strings.ttrFallsAsTextsGet}
          </p>
        </section>
      )}
      {/* Top repeated words */}
      {stats && stats.topWords.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.mostRepeatedWords}
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border border-b bg-muted/30">
                  <th className="p-3 font-medium text-foreground">#</th>
                  <th className="p-3 font-medium text-foreground">
                    {strings.word}
                  </th>
                  <th className="p-3 font-medium text-foreground">
                    {strings.count}
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.topWords.map((entry, index) => (
                  <tr className="border-border border-b" key={entry.word}>
                    <td className="p-3 text-muted-foreground">{index + 1}</td>
                    <td className="p-3 font-mono text-foreground">
                      {entry.word}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {fmt(entry.count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
